import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, map, take, tap } from 'rxjs/operators';
import { PixelCalculatorService } from '../../components/default-uploader/services/pixel-calculator.service';
import { removeAll as removeAllFiles, removeFile, setFiles } from '../files/actions/files.actions';
import {
  removeAllFilesResolution,
  removeFileResolution,
  setFilesResolution,
  updateFileResolution,
} from './files-resolution.actions';
import { selectIsResolutionDataProcessed } from './files-resolution.selectors';

@Injectable()
export class FilesResolutionEffects {
  constructor(
    private actions: Actions,
    private store: Store,
    private pixelCalculatorService: PixelCalculatorService,
  ) {}

  setFiles = createEffect(() => {
    return this.actions.pipe(
      ofType(setFiles),
      map(({ files }) => {
        const images = this.filterImages(files);
        const resolutionData = this.mapToResolutionData(images);
        return setFilesResolution({ files, resolutionData });
      }),
    );
  });

  calculateResolutionData = createEffect(
    () => {
      return this.actions.pipe(
        ofType(setFilesResolution),
        tap(({ files }) => {
          this.pixelCalculatorService.reset();

          this.pixelCalculatorService.fileProcessed$.subscribe((fileResolution) => {
            this.store.dispatch(
              updateFileResolution({
                update: { id: fileResolution.name, changes: fileResolution },
              }),
            );
          });

          this.pixelCalculatorService.calculate(files);

          this.store
            .select(selectIsResolutionDataProcessed)
            .pipe(filter(Boolean))
            .pipe(take(1))
            .subscribe(() => this.pixelCalculatorService.reset());
        }),
      );
    },
    { dispatch: false },
  );

  removeAllFiles = createEffect(() => {
    return this.actions.pipe(
      ofType(removeAllFiles),
      map(() => {
        return removeAllFilesResolution();
      }),
    );
  });

  removeFile = createEffect(() => {
    return this.actions.pipe(
      ofType(removeFile),
      map(({ name }) => {
        return removeFileResolution({ name });
      }),
    );
  });

  stopResolutionDataCalculation = createEffect(
    () => {
      return this.actions.pipe(
        ofType(removeAllFilesResolution),
        tap(() => {
          this.pixelCalculatorService.reset();
        }),
      );
    },
    { dispatch: false },
  );

  private mapToResolutionData(images: File[]) {
    return images.map((image) => ({
      name: image.name,
      width: 0,
      height: 0,
      processed: false,
    }));
  }

  private filterImages(files: File[]) {
    return files.filter((file) => file.type.includes('image/'));
  }
}
